import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type { BoardSession, Determination, DeterminationOutcome, Referral, ReferralStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const SECRETARY_ROLES = ['BOARD_SECRETARY'];
const READ_ROLES = ['BOARD_SECRETARY', 'BOARD_MEMBER'];

/**
 * S05 has no `GET /sessions` list — sessions convened from this browser are
 * cached locally (the real POST /sessions response) so they can be picked
 * when scheduling referrals. The referral queue and reviews-due queue are
 * genuine list endpoints.
 */
export function MedicalBoard() {
  const { persona } = useAuth();
  const isSecretary = persona?.roles.some((r) => SECRETARY_ROLES.includes(r)) ?? false;
  const canRead = persona?.roles.some((r) => READ_ROLES.includes(r)) ?? false;

  const [status, setStatus] = useState<ReferralStatus | ''>('REFERRED');
  const [sessions, setSessions] = useState<BoardSession[]>(() => loadList<BoardSession>('medboard.sessions'));
  const [showConvene, setShowConvene] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<Referral[] | null>(null);
  const [determineTarget, setDetermineTarget] = useState<Referral | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const queueQ = useApiQuery<{ items: Referral[] }>(canRead ? `/api/v1/medboard/queue${status ? `?status=${status}` : ''}` : null);
  const reviewsQ = useApiQuery<{ items: Determination[] }>(isSecretary ? '/api/v1/medboard/reviews/due' : null);

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Medical Board" />
        <ErrorNote message="This view requires the Medical Board Secretary or Board Member role." />
      </div>
    );
  }

  const markExamined = async (r: Referral) => {
    setBusyId(r.id);
    setError(null);
    try {
      await api.post(`/api/v1/medboard/referrals/${r.id}/examined`);
      queueQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <div>
      <PageHeader
        title="Assessments & Medical Board"
        subtitle="One scheduling and decision service for every medical determination — ASP disability/injury, home-care dependency"
        actions={
          isSecretary ? (
            <>
              <button className="btn-secondary" onClick={() => setShowConvene(true)}>
                + Convene session
              </button>
              <button
                className="btn-primary"
                disabled={selected.size === 0}
                onClick={() => setScheduleTarget((queueQ.data?.items ?? []).filter((r) => selected.has(r.id)))}
              >
                Schedule selected ({selected.size})
              </button>
            </>
          ) : undefined
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-4">
        <select className="field-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value as ReferralStatus)}>
          <option value="">All statuses</option>
          <option value="REFERRED">Referred</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="EXAMINED">Examined</option>
          <option value="DETERMINED">Determined</option>
        </select>
      </div>

      <div className="card overflow-x-auto">
        {queueQ.loading ? (
          <Spinner />
        ) : (queueQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Queue is empty" hint="Referrals arrive automatically from S02/S04/P03 claims that need a medical determination." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                {isSecretary ? <th className="table-head w-8" /> : null}
                <th className="table-head">NIN</th>
                <th className="table-head">Type</th>
                <th className="table-head">Source</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {queueQ.data!.items.map((r) => (
                <tr key={r.id}>
                  {isSecretary ? (
                    <td className="table-cell">
                      {r.status === 'REFERRED' ? <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /> : null}
                    </td>
                  ) : null}
                  <td className="table-cell text-xs">{r.nin}</td>
                  <td className="table-cell text-xs">{r.type}</td>
                  <td className="table-cell text-xs">
                    {r.sourceModule.toUpperCase()} · {r.sourceRef}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="table-cell text-right">
                    {isSecretary && r.status === 'SCHEDULED' ? (
                      <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => markExamined(r)} disabled={busyId === r.id}>
                        Mark examined
                      </button>
                    ) : isSecretary && r.status === 'EXAMINED' ? (
                      <button className="btn-primary !px-2 !py-1 text-xs" onClick={() => setDetermineTarget(r)}>
                        Determination
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isSecretary ? (
        <div className="mt-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Reviews due (periodic recall)</h2>
          <div className="card overflow-x-auto">
            {reviewsQ.loading ? (
              <Spinner />
            ) : (reviewsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No recalls due" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">NIN</th>
                    <th className="table-head">Outcome</th>
                    <th className="table-head">Review date</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewsQ.data!.items.map((d) => (
                    <tr key={d.id}>
                      <td className="table-cell text-xs">{d.referral?.nin}</td>
                      <td className="table-cell">
                        <StatusBadge status={d.outcome} />
                      </td>
                      <td className="table-cell text-xs text-slate-500">{formatDate(d.reviewDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {showConvene ? (
        <ConveneModal
          onClose={() => setShowConvene(false)}
          onDone={(s) => {
            setSessions(upsertList('medboard.sessions', s));
            setShowConvene(false);
          }}
        />
      ) : null}
      {scheduleTarget ? (
        <ScheduleModal
          referrals={scheduleTarget}
          sessions={sessions}
          onClose={() => setScheduleTarget(null)}
          onDone={() => {
            setScheduleTarget(null);
            setSelected(new Set());
            queueQ.reload();
          }}
        />
      ) : null}
      {determineTarget ? (
        <DetermineModal
          referral={determineTarget}
          onClose={() => setDetermineTarget(null)}
          onDone={() => {
            setDetermineTarget(null);
            queueQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ConveneModal({ onClose, onDone }: { onClose: () => void; onDone: (s: BoardSession) => void }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [venue, setVenue] = useState('ASP Head Office, Victoria');
  const [panel, setPanel] = useState('Dr Patsy Hoareau:Chair, Cindy Adeline:Secretary');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const session = await api.post<BoardSession>('/api/v1/medboard/sessions', {
        date,
        venue,
        panel: panel
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => {
            const [name, role] = s.split(':').map((x) => x.trim());
            return { name: name ?? s, role: role ?? 'Member' };
          }),
      });
      onDone(session);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not convene session');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Convene board session" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Date">
          <input className="field-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Venue">
          <input className="field-input" value={venue} onChange={(e) => setVenue(e.target.value)} />
        </Field>
        <Field label="Panel (name:role, comma-separated)">
          <input className="field-input" value={panel} onChange={(e) => setPanel(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !venue}>
            {busy ? 'Convening…' : 'Convene'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ScheduleModal({
  referrals,
  sessions,
  onClose,
  onDone,
}: {
  referrals: Referral[];
  sessions: BoardSession[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [sessionId, setSessionId] = useState(sessions[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/medboard/sessions/${sessionId}/schedule`, { referralIds: referrals.map((r) => r.id) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not schedule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Schedule ${referrals.length} referral(s)`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {sessions.length === 0 ? (
          <p className="text-sm text-amber-600">Convene a session first.</p>
        ) : (
          <Field label="Session">
            <select className="field-input" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {formatDate(s.date)} — {s.venue}
                </option>
              ))}
            </select>
          </Field>
        )}
        <p className="text-xs text-slate-500">This sends a WhatsApp appointment message to each claimant automatically.</p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !sessionId}>
            {busy ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DetermineModal({ referral, onClose, onDone }: { referral: Referral; onClose: () => void; onDone: () => void }) {
  const [outcome, setOutcome] = useState<DeterminationOutcome>('ACCEPTED');
  const [percentage, setPercentage] = useState('');
  const [reviewAfterMonths, setReviewAfterMonths] = useState('');
  const [minutesRef, setMinutesRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/medboard/determinations', {
        referralId: referral.id,
        outcome,
        percentage: percentage ? Number.parseFloat(percentage) : undefined,
        reviewAfterMonths: reviewAfterMonths ? Number.parseInt(reviewAfterMonths, 10) : undefined,
        minutesRef,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record determination');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Determination — ${referral.nin} (${referral.type})`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Outcome">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as DeterminationOutcome)}>
            <option value="ACCEPTED">Accepted</option>
            <option value="DECLINED">Declined</option>
            <option value="DEFERRED">Deferred</option>
          </select>
        </Field>
        <Field label="Percentage (disability/incapacity %, optional)">
          <input className="field-input" type="number" min={0} max={100} value={percentage} onChange={(e) => setPercentage(e.target.value)} />
        </Field>
        <Field label="Review recall (months, optional)">
          <input className="field-input" type="number" value={reviewAfterMonths} onChange={(e) => setReviewAfterMonths(e.target.value)} />
        </Field>
        <Field label="Minutes reference">
          <input className="field-input" value={minutesRef} onChange={(e) => setMinutesRef(e.target.value)} placeholder="e.g. MB-2026-014" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !minutesRef}>
            {busy ? 'Saving…' : 'Record determination'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
